import { SuggestionItem } from '../types';

/**
 * Dashboard metric suggestions — each maps to a CloudWatch dashboard tab
 * in the Overview section. These use the NAVIGATE queryId since they
 * render CloudWatch widgets (not GraphQL data).
 */
export const DASHBOARD_METRICS_SUGGESTIONS: SuggestionItem[] = [
  {
    id: 'dashboard.searchRate',
    label: 'Search Rate',
    description: 'Elasticsearch search request rate (queries/sec)',
    keywords: ['search rate', 'queries per second', 'qps', 'search requests'],
    category: 'domain-overview',
    queryId: 'CW_METRICS',
    fieldPath: 'cwMetrics',
    formatter: (v) => {
      if (!v || !v.timeStamps || v.timeStamps.length === 0) return '⚠️ No SearchRate data available.';
      const metrics = v.externalMetrics || [];
      if (!Array.isArray(metrics) || metrics.length === 0) return '⚠️ No SearchRate data returned.';
      const latest = metrics[0];
      const values = latest.values || [];
      const avg = values.length > 0 ? (values.reduce((a: number, b: number) => a + b, 0) / values.length).toFixed(2) : 'N/A';
      const max = values.length > 0 ? Math.max(...values).toFixed(2) : 'N/A';
      const min = values.length > 0 ? Math.min(...values).toFixed(2) : 'N/A';
      return [
        `## 📈 Search Rate`,
        `| Metric | Value |`,
        `|--------|-------|`,
        `| Label | ${latest.label || 'SearchRate'} |`,
        `| Average | ${avg} req/s |`,
        `| Max | ${max} req/s |`,
        `| Min | ${min} req/s |`,
        `| Data Points | ${values.length} |`,
      ].join('\n');
    },
  },
  {
    id: 'dashboard.indexingRate',
    label: 'Indexing Rate',
    description: 'Elasticsearch indexing rate (docs/sec)',
    keywords: ['indexing rate', 'docs per second', 'index rate', 'write rate'],
    category: 'domain-overview',
    queryId: 'CW_METRICS',
    fieldPath: 'cwMetrics',
    formatter: (v) => {
      if (!v || !v.timeStamps || v.timeStamps.length === 0) return '⚠️ No IndexingRate data available.';
      const metrics = v.externalMetrics || [];
      if (!Array.isArray(metrics) || metrics.length === 0) return '⚠️ No IndexingRate data returned.';
      const latest = metrics[0];
      const values = latest.values || [];
      const avg = values.length > 0 ? (values.reduce((a: number, b: number) => a + b, 0) / values.length).toFixed(2) : 'N/A';
      const max = values.length > 0 ? Math.max(...values).toFixed(2) : 'N/A';
      return [
        `## 📈 Indexing Rate`,
        `| Metric | Value |`,
        `|--------|-------|`,
        `| Average | ${avg} docs/s |`,
        `| Max | ${max} docs/s |`,
        `| Data Points | ${values.length} |`,
      ].join('\n');
    },
  },
  {
    id: 'dashboard.searchLatency',
    label: 'Search Latency',
    description: 'Elasticsearch search latency (ms)',
    keywords: ['search latency', 'query latency', 'p99 latency', 'response time'],
    category: 'domain-overview',
    queryId: 'CW_METRICS',
    fieldPath: 'cwMetrics',
    formatter: (v) => {
      if (!v || !v.timeStamps || v.timeStamps.length === 0) return '⚠️ No SearchLatency data available.';
      const metrics = v.externalMetrics || [];
      if (!Array.isArray(metrics) || metrics.length === 0) return '⚠️ No SearchLatency data returned.';
      const latest = metrics[0];
      const values = latest.values || [];
      const avg = values.length > 0 ? (values.reduce((a: number, b: number) => a + b, 0) / values.length).toFixed(2) : 'N/A';
      const max = values.length > 0 ? Math.max(...values).toFixed(2) : 'N/A';
      return [
        `## ⏱️ Search Latency`,
        `| Metric | Value |`,
        `|--------|-------|`,
        `| Average | ${avg} ms |`,
        `| Max | ${max} ms |`,
        `| Data Points | ${values.length} |`,
      ].join('\n');
    },
  },
  {
    id: 'dashboard.cpuUtilization',
    label: 'CPU Utilization',
    description: 'Cluster CPU utilization percentage',
    keywords: ['cpu', 'cpu utilization', 'cpu usage', 'processor'],
    category: 'domain-overview',
    queryId: 'CW_METRICS',
    fieldPath: 'cwMetrics',
    formatter: (v) => {
      if (!v || !v.timeStamps || v.timeStamps.length === 0) return '⚠️ No CPUUtilization data available.';
      const metrics = v.externalMetrics || [];
      if (!Array.isArray(metrics) || metrics.length === 0) return '⚠️ No CPUUtilization data returned.';
      const latest = metrics[0];
      const values = latest.values || [];
      const avg = values.length > 0 ? (values.reduce((a: number, b: number) => a + b, 0) / values.length).toFixed(2) : 'N/A';
      const max = values.length > 0 ? Math.max(...values).toFixed(2) : 'N/A';
      return [
        `## 🖥️ CPU Utilization`,
        `| Metric | Value |`,
        `|--------|-------|`,
        `| Average | ${avg}% |`,
        `| Max | ${max}% |`,
        `| Data Points | ${values.length} |`,
      ].join('\n');
    },
  },
  {
    id: 'dashboard.jvmMemoryPressure',
    label: 'JVM Memory Pressure',
    description: 'JVM heap memory pressure percentage',
    keywords: ['jvm', 'memory pressure', 'heap', 'jvm memory', 'gc pressure'],
    category: 'domain-overview',
    queryId: 'CW_METRICS',
    fieldPath: 'cwMetrics',
    formatter: (v) => {
      if (!v || !v.timeStamps || v.timeStamps.length === 0) return '⚠️ No JVMMemoryPressure data available.';
      const metrics = v.externalMetrics || [];
      if (!Array.isArray(metrics) || metrics.length === 0) return '⚠️ No JVMMemoryPressure data returned.';
      const latest = metrics[0];
      const values = latest.values || [];
      const avg = values.length > 0 ? (values.reduce((a: number, b: number) => a + b, 0) / values.length).toFixed(2) : 'N/A';
      const max = values.length > 0 ? Math.max(...values).toFixed(2) : 'N/A';
      return [
        `## 🧠 JVM Memory Pressure`,
        `| Metric | Value |`,
        `|--------|-------|`,
        `| Average | ${avg}% |`,
        `| Max | ${max}% |`,
        `| Data Points | ${values.length} |`,
      ].join('\n');
    },
  },
  {
    id: 'dashboard.clusterUsedSpace',
    label: 'Cluster Used Space',
    description: 'Total cluster storage used (GB)',
    keywords: ['cluster used space', 'storage', 'disk used', 'cluster storage'],
    category: 'domain-overview',
    queryId: 'CW_METRICS',
    fieldPath: 'cwMetrics',
    formatter: (v) => {
      if (!v || !v.timeStamps || v.timeStamps.length === 0) return '⚠️ No ClusterUsedSpace data available.';
      const metrics = v.externalMetrics || [];
      if (!Array.isArray(metrics) || metrics.length === 0) return '⚠️ No data returned.';
      const latest = metrics[0];
      const values = latest.values || [];
      const latestValue = values.length > 0 ? values[values.length - 1].toFixed(2) : 'N/A';
      return `**Cluster Used Space:** ${latestValue} GB`;
    },
  },
  {
    id: 'dashboard.4xxErrors',
    label: '4xx Errors',
    description: 'HTTP 4xx error count',
    keywords: ['4xx', '400', 'client errors', 'bad request', 'forbidden'],
    category: 'domain-overview',
    queryId: 'CW_METRICS',
    fieldPath: 'cwMetrics',
    formatter: (v) => {
      if (!v || !v.timeStamps || v.timeStamps.length === 0) return '⚠️ No 4xx error data available.';
      const metrics = v.externalMetrics || [];
      if (!Array.isArray(metrics) || metrics.length === 0) return '✅ No 4xx errors in the selected period.';
      const latest = metrics[0];
      const values = latest.values || [];
      const total = values.reduce((a: number, b: number) => a + b, 0);
      return total > 0
        ? `⚠️ **4xx Errors:** ${total.toFixed(0)} total in the selected period (avg ${(total / values.length).toFixed(1)}/interval)`
        : '✅ **4xx Errors:** 0 in the selected period';
    },
  },
  {
    id: 'dashboard.5xxErrors',
    label: '5xx Errors',
    description: 'HTTP 5xx error count',
    keywords: ['5xx', '500', 'server errors', 'internal server error'],
    category: 'domain-overview',
    queryId: 'CW_METRICS',
    fieldPath: 'cwMetrics',
    formatter: (v) => {
      if (!v || !v.timeStamps || v.timeStamps.length === 0) return '⚠️ No 5xx error data available.';
      const metrics = v.externalMetrics || [];
      if (!Array.isArray(metrics) || metrics.length === 0) return '✅ No 5xx errors in the selected period.';
      const latest = metrics[0];
      const values = latest.values || [];
      const total = values.reduce((a: number, b: number) => a + b, 0);
      return total > 0
        ? `🚨 **5xx Errors:** ${total.toFixed(0)} total in the selected period (avg ${(total / values.length).toFixed(1)}/interval)`
        : '✅ **5xx Errors:** 0 in the selected period';
    },
  },
  // Keep navigational suggestions for complex dashboard tabs
  {
    id: 'dashboard.esCustomer',
    label: 'All ES Customer Metrics (Dashboard)',
    description: 'Full Elasticsearch customer metrics dashboard with all graphs',
    keywords: ['all customer metrics', 'full dashboard', 'all es metrics'],
    category: 'domain-overview',
    queryId: 'NAVIGATE',
    fieldPath: '',
    formatter: () => '📊 **All ES Customer Metrics** — Switch to Overview → Elasticsearch (Customer metrics) tab to view all graphs with interactive time range.',
  },
  {
    id: 'dashboard.ec2',
    label: 'EC2 Metrics (Dashboard)',
    description: 'Full EC2 metrics dashboard',
    keywords: ['ec2 dashboard', 'ec2 graphs', 'all ec2 metrics'],
    category: 'domain-overview',
    queryId: 'NAVIGATE',
    fieldPath: '',
    formatter: () => '📊 **EC2 Metrics Dashboard** — Switch to Overview → EC2 tab for per-instance CPUUtilization, NetworkIn/Out, DiskOps graphs.',
  },
  {
    id: 'dashboard.ebs',
    label: 'EBS Metrics (Dashboard)',
    description: 'Full EBS metrics dashboard',
    keywords: ['ebs dashboard', 'ebs graphs', 'all ebs metrics', 'volume metrics'],
    category: 'domain-overview',
    queryId: 'NAVIGATE',
    fieldPath: '',
    formatter: () => '📊 **EBS Metrics Dashboard** — Switch to Overview → EBS tab for VolumeReadOps, VolumeWriteOps, Throughput, BurstBalance.',
  },
  {
    id: 'dashboard.haproxy',
    label: 'HAProxy Metrics (Dashboard)',
    description: 'Full HAProxy metrics dashboard',
    keywords: ['haproxy dashboard', 'haproxy graphs', 'proxy dashboard'],
    category: 'domain-overview',
    queryId: 'NAVIGATE',
    fieldPath: '',
    formatter: () => '📊 **HAProxy Metrics Dashboard** — Switch to Overview → HAProxy (Internal) tab.',
  },
];
